import React, { useEffect, useState, useRef } from "react";
import { ShoppingCart, Package, ArrowRight, Eye } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";


const Shop = ({ initialProducts = [], categories = [], onCategoryChange, loading: globalLoading = false }) => {
  const { addToCart } = useCart();
  const [activeCategory, setActiveCategory] = useState("ALL");
  const [products, setProducts] = useState(initialProducts);
  const [localLoading, setLocalLoading] = useState(false);

  // Combine global and local loading states
  const isLoading = globalLoading || localLoading;
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    if (initialProducts && initialProducts.length > 0) {
      setProducts(initialProducts);
      // Failsafe: if we have products and they are not hidden by loading, ensure they become visible
      if (!globalLoading) {
        setIsVisible(true);
      }
    }
  }, [initialProducts, globalLoading]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    // Use a more robust observer configuration
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) {
          setIsVisible(true);
          observer.unobserve(section);
        }
      },
      { 
        threshold: [0, 0.1, 0.2],
        rootMargin: "50px" 
      },
    );

    observer.observe(section);

    // Immediate check for visibility
    const rect = section.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setIsVisible(true);
    }

    return () => {
      if (section) observer.unobserve(section);
      observer.disconnect();
    };
  }, []);

  const handleCategoryChange = async (categoryId) => {
    setActiveCategory(categoryId);
    setLocalLoading(true);
    try {
      if (onCategoryChange) {
        const result = await onCategoryChange(categoryId);
        if (result) setProducts(result);
      }
    } catch (error) {
      console.error("Failed to filter products", error);
    } finally {
      setLocalLoading(false);
    }
  };

  const SkeletonCard = () => (
    <div className="bg-white border border-black/10 overflow-hidden animate-pulse">
      <div className="aspect-square bg-gray-200" />
      <div className="p-5 space-y-4">
        <div className="h-3 bg-gray-200 w-20 rounded" />
        <div className="h-5 bg-gray-200 w-full rounded" />
        <div className="h-6 bg-gray-200 w-24 rounded" />
        <div className="h-12 bg-gray-200 w-full rounded" />
      </div>
    </div>
  );

  const handleAddToCart = (product) => {
    addToCart(product);
  };

  const displayCategories = [{ id: "ALL", name: "ALL" }, ...categories];

  return (
    <section
      ref={sectionRef}
      id="shop"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-[#fafafa] overflow-x-hidden"
      style={{
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <div className="max-w-[1400px] mx-auto">
        {/* Section header */}
        <div
          className={`text-center mb-12 sm:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2
            className="text-3xl sm:text-4xl lg:text-6xl font-normal text-black mb-3 sm:mb-4"
            style={{
              fontFamily: "Playfair Display, Georgia, serif",
              fontWeight: 300,
              letterSpacing: "-0.01em",
            }}
          >
            Shop
          </h2>
          <p
            className="text-base sm:text-lg text-black max-w-[600px] mx-auto px-4"
            style={{
              fontFamily: "Crimson Pro, Georgia, serif",
              fontStyle: "italic",
            }}
          >
            Take a piece of Temidayo's music home with you
          </p>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12 sm:mb-16">
          {displayCategories.map(cat => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id)}
              className={`px-4 py-2 text-[10px] font-bold tracking-widest uppercase rounded-full transition-all duration-300 ${activeCategory === cat.id
                ? "bg-black text-white shadow-lg"
                : "bg-white text-black/85 border border-black/20 hover:border-black/30"
                }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Products grid */}
        <div className="min-h-[400px]">
          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[1, 2, 3, 4].map((i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : (products && products.length > 0) ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {products.map((product, index) => (
                <article
                  key={index}
                  className={`group bg-white border border-black/10 overflow-hidden transition-all duration-700 hover:shadow-[0_12px_40px_rgba(0,0,0,0.12)] hover:-translate-y-2 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {/* Product image */}
                  <div className="relative aspect-square overflow-hidden bg-black/5">
                    <img
                      src={product.image}
                      alt={product.title}
                      className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                      loading="lazy"
                    />

                    {/* Quick view overlay */}
                    <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col gap-2 p-4 justify-center items-center">
                      <button
                        onClick={() => handleAddToCart(product)}
                        disabled={product.stock_quantity === 0 && product.category_type !== "Music Content"}
                        className="w-full bg-white text-black py-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-[2px] hover:bg-jazz-brown hover:text-white transition-all duration-300 shadow-xl group/btn disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ShoppingCart size={14} className="group-hover/btn:translate-x-1 transition-transform" />
                        Add to Cart
                      </button>
                      <Link
                        to={`/shop/product/${product.slug}`}
                        className="w-full bg-black/40 backdrop-blur-md text-white py-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-bold uppercase tracking-[2px] border border-white/10 hover:bg-black transition-all duration-300"
                      >
                        <Eye size={14} />
                        Details
                      </Link>
                    </div>
                  </div>

                  {/* Product info */}
                  <div className="p-4 sm:p-5">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[9px] font-bold tracking-widest text-jazz-brown uppercase">
                        {product.category}
                      </span>
                      <span className="text-sm font-bold text-black font-serif">
                        ${Number(product.price).toFixed(2)}
                      </span>
                    </div>

                    <h3
                      className="text-base font-medium text-black mb-4 line-clamp-1 group-hover:text-jazz-brown transition-colors duration-300"
                      style={{
                        fontFamily: "Playfair Display, Georgia, serif",
                      }}
                    >
                      {product.title}
                    </h3>

                    <button
                      onClick={() => handleAddToCart(product)}
                      disabled={product.stock_quantity === 0 && product.category_type !== "Music Content"}
                      className="w-full bg-black text-white py-3 flex items-center justify-center gap-2 hover:bg-jazz-brown transition-all duration-500 group/btn disabled:opacity-30 disabled:cursor-not-allowed"
                      style={{
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "2px",
                      }}
                    >
                      <ShoppingCart
                        size={14}
                        className="transition-transform duration-300 group-hover/btn:scale-110"
                      />
                      ADD TO CART
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="col-span-full py-20 text-center border border-dashed border-black/20 rounded-xl bg-white/50 w-full">
              <Package size={48} className="mx-auto text-black/60 mb-4" />
              <p className="text-black font-serif italic text-sm uppercase tracking-widest">No products found in this category.</p>
            </div>
          )}
        </div>

        {/* View shop link */}
        <div
          className={`text-center mt-12 sm:mt-16 transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <Link
            to="/shop"
            className="inline-flex items-center gap-3 text-black font-bold tracking-[3px] uppercase text-[10px] group py-4 px-10 bg-white border border-black/5 rounded-2xl hover:bg-black hover:text-white transition-all duration-500 shadow-sm"
          >
            Browse Full Shop
            <ArrowRight size={14} className="group-hover:translate-x-2 transition-transform duration-500" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Shop;
